import { motion, useMotionValue, useTransform } from "motion/react";
import { cn } from "../utils/cn.js";
import { useIsFlat, useIsGummy } from "../hooks/useGummyTheme.js";

type CheckItemProps = {
  isChecked: boolean;
  handleChecked: (isChecked: PointerEvent) => void;
  showCheckmarkHint?: boolean;
  disabled?: boolean;
  className?: string;
};

const LegacyCheckmark = ({
  isChecked,
  handleChecked,
  showCheckmarkHint = false,
  disabled = false,
  className
}: CheckItemProps) => {
  const pathLength = useMotionValue(0);
  const opacity = useTransform(pathLength, [0.05, 0.15], [0, 1]);
  const onCheck = (e: PointerEvent) => {
    if (!disabled) {
      handleChecked(e);
    }
  };
  return (
    <motion.button
      initial={false}
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        transformOrigin: "50% 0%"
      }}
      animate={{
        scale: isChecked ? 0.9 : 0.7,
        opacity: disabled ? 0.5 : isChecked ? 1 : 0.9
      }}
      className={cn(
        `${
          isChecked
            ? `border-emerald-500 bg-emerald-200 text-emerald-800 dark:bg-emerald-900 dark:hover:bg-emerald-900 `
            : ` bg-mono-200 dark:bg-mono-950 border-mono-400 dark:hover:bg-mono-900 hover:border-emerald-400 hover:bg-emerald-50`
        } checkmark dark:border-mono-700 aspect-1/1 relative mt-2.5 items-center justify-center rounded-md border transition-colors`,
        className
      )}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      onTap={onCheck}
    >
      {showCheckmarkHint && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox={`0 0 150 150`}
          className={` dark:text-mono-500 absolute h-full w-full ${
            isChecked && `hidden`
          } `}
        >
          <motion.path
            d="M38 74.707l24.647 24.646L116.5 45.5"
            fill="transparent"
            strokeWidth="10"
            stroke="currentColor"
            strokeLinecap="round"
            animate={{
              pathLength: [0, 0.9, 0],
              opacity: [0, 0.4, 0],
              scale: [0.7, 1, 0.7]
            }}
            transition={{
              repeat: Infinity,
              repeatDelay: 10,
              duration: 2,
              delay: 3
            }}
          />
        </svg>
      )}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 150 150`}
        className={`h-full w-full dark:text-emerald-500 `}
      >
        <motion.path
          d="M38 74.707l24.647 24.646L116.5 45.5"
          fill="transparent"
          strokeWidth="10"
          stroke="currentColor"
          strokeLinecap="round"
          animate={{ pathLength: isChecked ? 0.9 : 0 }}
          style={{ pathLength, opacity }}
        />
      </svg>
    </motion.button>
  );
};

const GummyCheckmark = ({
  isChecked,
  handleChecked,
  showCheckmarkHint = false,
  disabled = false,
  className
}: CheckItemProps) => {
  const pathLength = useMotionValue(0);
  const opacity = useTransform(pathLength, [0.05, 0.15], [0, 1]);
  const isFlat = useIsFlat();
  const onCheck = (e: PointerEvent) => {
    if (!disabled) {
      handleChecked(e);
    }
  };

  const checkedSurface = cn("bg-tile-success text-white", "shadow-raised");

  const uncheckedSurface = cn(
    "bg-mono-200 text-emerald-500",
    "dark:bg-mono-800 dark:text-emerald-400",
    "shadow-sunken",
    "hover:bg-emerald-100 dark:hover:bg-emerald-950/60"
  );

  return (
    <motion.button
      type="button"
      initial={false}
      aria-pressed={isChecked}
      disabled={disabled}
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        transformOrigin: "50% 50%"
      }}
      animate={{
        scale: isChecked ? 1 : 0.9,
        opacity: disabled ? 0.5 : 1
      }}
      whileTap={disabled ? undefined : { scale: 0.82 }}
      whileHover={disabled || isFlat ? undefined : { y: -2 }}
      className={cn(
        "relative mt-2.5 flex aspect-square items-center justify-center overflow-hidden rounded-surface-sm",
        "outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40",
        isChecked ? checkedSurface : uncheckedSurface,
        className
      )}
      transition={{ type: "spring", stiffness: 400, damping: 22 }}
      onTap={onCheck}
    >
      {isChecked && <span aria-hidden className="top-gloss" />}
      {showCheckmarkHint && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 150 150"
          className={cn(
            "absolute h-full w-full text-emerald-400 dark:text-emerald-500/70",
            isChecked && "hidden"
          )}
        >
          <motion.path
            d="M38 74.707l24.647 24.646L116.5 45.5"
            fill="transparent"
            strokeWidth="14"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            animate={{
              pathLength: [0, 0.9, 0],
              opacity: [0, 0.5, 0],
              scale: [0.7, 1, 0.7]
            }}
            transition={{
              repeat: Infinity,
              repeatDelay: 10,
              duration: 2,
              delay: 3
            }}
          />
        </svg>
      )}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 150 150"
        className="relative h-full w-full"
        style={{
          filter: isChecked
            ? "drop-shadow(0 1px 1px rgba(0,0,0,0.25))"
            : undefined
        }}
      >
        <motion.path
          d="M38 74.707l24.647 24.646L116.5 45.5"
          fill="transparent"
          strokeWidth="14"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{ pathLength: isChecked ? 0.9 : 0 }}
          style={{ pathLength, opacity }}
        />
      </svg>
    </motion.button>
  );
};

const Checkmark = (props: CheckItemProps) => {
  const isGummy = useIsGummy();
  return isGummy ? (
    <GummyCheckmark {...props} />
  ) : (
    <LegacyCheckmark {...props} />
  );
};

export default Checkmark;
